/**
 * The cockpit's theme hooks on the theme of @digitalisation/enlixy-comp.
 *
 * - Colours: the package's tokens.css (imported in index.css), replaced per
 *   organization by applyBranding(). Nothing here sets CSS variables, so a
 *   branding always applies.
 * - Light, dark or system: the package's theme store (initTheme, `.dark` on
 *   <html>), with the cockpit's storage key so users keep their choice.
 * - `useTheme().currentTheme` is the JS copy of the same colours
 *   (styles/themes.ts, kept equal to tokens.css by themes.test.ts) for
 *   Highcharts and inline styles; `useThemeClasses()` gives Tailwind classes.
 */

import {
  initTheme,
  useTheme as useSharedTheme,
} from "@digitalisation/enlixy-comp";
import { getTheme, type Theme, type ThemeName } from "../styles/themes";

/** Where the choice is stored: the key the cockpit has always used. */
export const THEME_STORAGE_KEY = "performanceCockpit-theme-preference";
/** Set once the cockpit's default (light) was stored for a browser without a choice. */
const DEFAULT_KEPT_KEY = "performanceCockpit-theme-default.v1";

/**
 * Applies the stored theme before the first render (main.tsx). Without a
 * stored choice the package follows the system; the cockpit always started
 * light, so "light" is stored once for a browser without a choice. A later
 * "System" choice is kept.
 */
export function initCockpitTheme(): void {
  try {
    if (!localStorage.getItem(DEFAULT_KEPT_KEY)) {
      if (localStorage.getItem(THEME_STORAGE_KEY) === null) {
        localStorage.setItem(THEME_STORAGE_KEY, "light");
      }
      localStorage.setItem(DEFAULT_KEPT_KEY, "1");
    }
  } catch {
    // private mode: the package's default (system) applies
  }
  initTheme({ storageKey: THEME_STORAGE_KEY });
}

export interface CockpitTheme {
  /** JS colour values of the theme in effect (charts, inline styles) */
  currentTheme: Theme;
  themeName: ThemeName;
  isDark: boolean;
}

/** The theme in effect; the component renders again when it changes. */
export const useTheme = (): CockpitTheme => {
  const { dark } = useSharedTheme();
  const themeName: ThemeName = dark ? "dark" : "light";
  return { currentTheme: getTheme(themeName), themeName, isDark: dark };
};

/**
 * Theme-aware class names. Brand, link and focus colours use the package's
 * utilities (bg-brand, text-accent …), so they follow an organization's
 * branding; the neutral and status colours are Tailwind palette classes.
 */
export const useThemeClasses = () => {
  const { isDark } = useTheme();

  return {
    // Background utilities
    bgPrimary: isDark ? "bg-slate-800" : "bg-white",
    bgSecondary: isDark ? "bg-slate-700" : "bg-gray-50",
    bgTertiary: isDark ? "bg-slate-600" : "bg-gray-100",
    bgSurface: isDark ? "bg-slate-800" : "bg-white",
    bgElevated: isDark ? "bg-slate-700" : "bg-white",

    // Text utilities
    textPrimary: isDark ? "text-slate-50" : "text-gray-900",
    textSecondary: isDark ? "text-slate-200" : "text-gray-700",
    textTertiary: isDark ? "text-slate-300" : "text-gray-600",
    textMuted: isDark ? "text-slate-400" : "text-gray-500",
    textWhite: "text-white",
    textInverse: isDark ? "text-slate-900" : "text-white",

    // Status colors
    textSuccess: isDark ? "text-emerald-400" : "text-green-500",
    textWarning: isDark ? "text-yellow-400" : "text-yellow-500",
    textError: isDark ? "text-red-400" : "text-red-500",
    textInfo: isDark ? "text-blue-400" : "text-blue-500",

    bgSuccess: isDark ? "bg-emerald-400" : "bg-green-500",
    bgWarning: isDark ? "bg-yellow-400" : "bg-yellow-500",
    bgError: isDark ? "bg-red-400" : "bg-red-500",
    bgInfo: isDark ? "bg-blue-400" : "bg-blue-500",

    // Status background variants (subtle)
    bgSuccessSubtle: isDark ? "bg-emerald-900/20" : "bg-green-50",
    bgWarningSubtle: isDark ? "bg-yellow-900/20" : "bg-yellow-50",
    bgErrorSubtle: isDark ? "bg-red-900/20" : "bg-red-50",
    bgInfoSubtle: isDark ? "bg-blue-900/20" : "bg-blue-50",

    // Additional semantic colors for UI elements
    bgPurple: isDark ? "bg-purple-400" : "bg-purple-500",
    bgOrange: isDark ? "bg-orange-400" : "bg-orange-500",
    bgIndigo: isDark ? "bg-indigo-400" : "bg-indigo-500",
    bgTeal: isDark ? "bg-teal-400" : "bg-teal-500",
    bgPink: isDark ? "bg-pink-400" : "bg-pink-500",
    bgGray: isDark ? "bg-slate-500" : "bg-gray-500",

    textPurple: isDark ? "text-purple-400" : "text-purple-600",
    textOrange: isDark ? "text-orange-400" : "text-orange-600",
    textIndigo: isDark ? "text-indigo-400" : "text-indigo-600",
    textTeal: isDark ? "text-teal-400" : "text-teal-600",
    textPink: isDark ? "text-pink-400" : "text-pink-600",
    textGray: isDark ? "text-slate-400" : "text-gray-600",

    // Links: the text-safe brand accent (follows the branding)
    linkPrimary: "text-accent dark:text-accent-on-dark",
    linkHover: "hover:text-accent-dark dark:hover:text-fg",

    // Subtle background variants for different colors
    bgPurpleSubtle: isDark ? "bg-purple-900/20" : "bg-purple-50",
    bgOrangeSubtle: isDark ? "bg-orange-900/20" : "bg-orange-50",
    bgIndigoSubtle: isDark ? "bg-indigo-900/20" : "bg-indigo-50",
    bgTealSubtle: isDark ? "bg-teal-900/20" : "bg-teal-50",
    bgPinkSubtle: isDark ? "bg-pink-900/20" : "bg-pink-50",
    bgGraySubtle: isDark ? "bg-slate-800/30" : "bg-gray-50",

    // Section-specific color schemes (for HelpView sections)
    sectionBlue: isDark
      ? "bg-blue-900/20 border-blue-500/30"
      : "bg-blue-50 border-blue-200",
    sectionGreen: isDark
      ? "bg-emerald-900/20 border-emerald-500/30"
      : "bg-green-50 border-green-200",
    sectionYellow: isDark
      ? "bg-yellow-900/20 border-yellow-500/30"
      : "bg-yellow-50 border-yellow-200",
    sectionRed: isDark
      ? "bg-red-900/20 border-red-500/30"
      : "bg-red-50 border-red-200",
    sectionPurple: isDark
      ? "bg-purple-900/20 border-purple-500/30"
      : "bg-purple-50 border-purple-200",
    sectionOrange: isDark
      ? "bg-orange-900/20 border-orange-500/30"
      : "bg-orange-50 border-orange-200",

    // Border utilities
    borderPrimary: isDark ? "border-slate-600" : "border-gray-200",
    borderSecondary: isDark ? "border-slate-500" : "border-gray-300",
    borderAccent: isDark ? "border-blue-500" : "border-blue-200",
    borderTree: isDark ? "border-slate-400" : "border-gray-300", // Specific for tree hierarchy lines

    // Brand colors (follow the branding)
    bgBrand: "bg-brand",
    textBrand: "text-accent-dark dark:text-accent-on-dark",
    bgBrandLight: "bg-brand-light",
    textBrandLight: "text-accent dark:text-accent-on-dark",

    // Interactive utilities
    hover: isDark ? "hover:bg-slate-700" : "hover:bg-gray-100",
    hoverSecondary: isDark ? "hover:bg-slate-600" : "hover:bg-gray-200",
    active: isDark ? "bg-slate-600" : "bg-gray-200",
    focus: "focus:ring-accent",

    // Card and surface utilities
    cardBg: isDark ? "bg-slate-800" : "bg-white",
    cardBorder: isDark ? "border-slate-600" : "border-gray-200",
    cardShadow: isDark ? "shadow-lg shadow-black/20" : "shadow-sm",

    // Input utilities
    inputBg: isDark ? "bg-slate-700" : "bg-white",
    inputBorder: isDark ? "border-slate-600" : "border-gray-300",
    inputFocus: "focus:border-accent focus:ring-accent",
  };
};

/**
 * Semantic colour values of the theme in effect (chart libraries, the What's
 * New frame). Brand values here are the Focke defaults, not the branding.
 */
export const useThemeColorValues = () => {
  const { currentTheme } = useTheme();
  return {
    // Status colors
    success: currentTheme.colors.status.success,
    warning: currentTheme.colors.status.warning,
    error: currentTheme.colors.status.error,
    info: currentTheme.colors.status.info,
    disabled: currentTheme.colors.status.disabled,

    // Brand colors
    brand: currentTheme.colors.title,
    brandLight: currentTheme.colors.cockpitBlue.light,
    brandDark: currentTheme.colors.cockpitBlue.dark,

    // Background colors
    bgPrimary: currentTheme.colors.background.primary,
    bgSecondary: currentTheme.colors.background.secondary,
    bgSurface: currentTheme.colors.background.surface,

    // Text colors
    textPrimary: currentTheme.colors.text.primary,
    textSecondary: currentTheme.colors.text.secondary,
    textMuted: currentTheme.colors.text.muted,

    // Border colors
    borderPrimary: currentTheme.colors.border.primary,
    borderSecondary: currentTheme.colors.border.secondary,
  };
};
